import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskedLines, Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { JsonLd } from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import {
  Sparkles,
  PenTool,
  Blocks,
  TrendingUp,
} from "lucide-react";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About Dorado9",
  url: "https://dorado9.com/about",
  description:
    "Learn how Dorado9 combines digital marketing, creative strategy, technology and design to help ambitious businesses grow online.",
  publisher: { "@id": "https://dorado9.com/#organization" },
  mainEntity: {
    "@type": "Organization",
    "@id": "https://dorado9.com/#organization",
    name: "Dorado9",
    url: "https://dorado9.com",
    logo: "https://dorado9.com/favicon.png",
    email: "hello@dorado9.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Amritsar",
      addressRegion: "Punjab",
      addressCountry: "IN",
    },
  },
};

export default function About()
 {
  return (
    <>
      <JsonLd id="about-page" data={aboutSchema} />
      <PageHero
  eyebrow="The Studio"
  image="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=60&auto=format&fit=crop"
  lines={[
    <>A studio for</>,
    <>brand <span className="font-serif-accent text-brand-purple lowercase">growth.</span></>
  ]}
>
  <div className="mt-12 max-w-xl">
    <p className="text-lg lg:text-xl leading-relaxed text-zinc-400">
      We partner with ambitious brands to create memorable identities,
      high-performing websites and marketing that delivers measurable
      business growth—not just impressions and clicks.
    </p>
  </div>
</PageHero>
    <section className="relative overflow-hidden bg-brand-bg py-24 lg:py-36 px-6 lg:px-12 border-t border-zinc-900">
  {/* Ambient glow */}
  <div
    className="
      absolute
      -left-48
      top-1/3
      w-[650px]
      h-[650px]
      rounded-full
      blur-[180px]
      opacity-[0.08]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.9), transparent 70%)",
    }}
  />

  <div className="relative z-10 max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-16 lg:gap-24">

    {/* LEFT */}
    <div className="lg:col-span-5">

      <Reveal>
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-8 bg-brand-purple" />

          <p className="eyebrow m-0">
            Chapter 01 — The Beginning
          </p>
        </div>

        <h2
          className="
            font-display
            text-[3.5rem]
            sm:text-6xl
            lg:text-[5.2vw]
            xl:text-[5rem]
            tracking-tighter
            uppercase
            leading-[0.86]
          "
        >
          MORE THAN
          <br />
          A STUDIO.
        </h2>

        <h3
          className="
            mt-4
            font-serif-accent
            text-brand-mint
            text-[3.1rem]
            sm:text-5xl
            lg:text-[4.2vw]
            xl:text-[4.2rem]
            leading-[0.9]
            tracking-tight
          "
        >
          ...and miles to go
          <br />
          before I sleep.
        </h3>
      </Reveal>


      {/* MASCOT */}
      <Reveal delay={0.15}>
        <div className="relative mt-10 lg:mt-14">

        

          {/* glow behind mascot */}
          <div
            className="
              absolute
              left-1/2
              bottom-10
              -translate-x-1/2
              w-[280px]
              h-[180px]
              rounded-full
              blur-[100px]
              opacity-20
              pointer-events-none
            "
            style={{
              background:
                "radial-gradient(circle, rgba(102,76,221,.8), transparent 70%)",
            }}
          />

          <img
            src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/07/cdsc-scaled-e1784910956880.png"
            alt="Dorothy's cat mascot"
            className="
              relative
              z-10
              w-full
              max-w-[470px]
              h-auto
              object-contain
              transition-transform
              duration-700
              hover:scale-[1.02]
            "
          />

        </div>
      </Reveal>

    </div>


    {/* RIGHT */}
    <Reveal
      className="lg:col-span-6 lg:col-start-7 lg:pt-3"
      delay={0.15}
    >
      <div className="relative">

        {/* vertical editorial line */}
        <div
          className="
            absolute
            -left-7
            top-1
            bottom-1
            hidden
            lg:block
            w-px
            bg-gradient-to-b
            from-brand-mint/50
            via-zinc-800
            to-transparent
          "
        />

        <div className="space-y-8 text-zinc-300 leading-relaxed text-base lg:text-[18px] lg:leading-[1.85]">

          <p className="text-zinc-300">
            Hi, I'm Dorothy Sood, and Dorado9 is my little corner of the
            internet. What began as a one-person freelance venture in 2025
            has slowly grown into a creative studio built around one thing
            I genuinely enjoy:
            <span className="text-white">
              {" "}solving business problems through marketing.
            </span>
          </p>

          <p>
            The funny part is, I never planned on ending up here. After
            graduating in science, I explored a few different career paths
            before an acquaintance casually suggested digital marketing.
            I gave it a shot, discovered a world where creativity meets
            data, and somehow never looked back.
          </p>

          <p>
            I love that this field lets me think like both an artist and an
            engineer. One moment I'm obsessing over typography, the next I'm
            buried in analytics, ad campaigns, SEO audits, or conversion
            funnels. That balance is exactly what keeps me hooked.
          </p>

          <p>
            And the name?
            <span className="text-white"> Dorado9 </span>
            has its own little story. "Dorado" comes from the dolphin fish,
            a playful nod to a childhood nickname that somehow stuck with
            me. The
            <span className="text-white"> 9 </span>
            ? That's just my lucky number. 😉
          </p>

        </div>


        {/* Small signature block */}
        <div className="mt-14 pt-7 border-t border-white/[0.07] flex items-end justify-between gap-6">

          <div>
            <p className="text-[9px] uppercase tracking-[0.28em] text-zinc-600">
              Founded
            </p>

            <p className="mt-2 font-display text-xl uppercase">
              2025
            </p>
          </div>

          <div className="text-right">
            <p className="text-[9px] uppercase tracking-[0.28em] text-zinc-600">
              Based in
            </p>

            <p className="mt-2 font-display text-xl uppercase">
              Punjab, India
            </p>
          </div>

        </div>

      </div>
    </Reveal>

  </div>
</section>
<section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900">
  <div className="max-w-[1600px] mx-auto">

    <Reveal className="mb-20">
      <p className="eyebrow mb-6">
        Chapter 02 — My Philosophy
      </p>

      <h2 className="font-display text-4xl lg:text-6xl tracking-tighter uppercase leading-[0.95] max-w-4xl">
        HOW we THINK.
        <br></br><span className="font-serif-accent text-brand-mint lowercase">
          {" "}How we build.
        </span>
      </h2>
    </Reveal>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">

      {[
        {
          icon: Sparkles,
          title: "Curiosity.",
          text: "Every meaningful project starts with asking better questions before chasing better answers.",
        },
        {
          icon: PenTool,
          title: "Craft.",
          text: "Good design isn't decoration. Every pixel, word and interaction should earn its place.",
        },
        {
          icon: Blocks,
          title: "Systems.",
          text: "I don't build campaigns. I build repeatable systems that continue working long after launch.",
        },
        {
          icon: TrendingUp,
          title: "Momentum.",
          text: "Small improvements compound. Sustainable growth always beats overnight success.",
        },
      ].map((item, i) => {
        const Icon = item.icon;

        return (
          <Reveal key={item.title} delay={i * 0.08}>
            <div>

              <Icon
                size={30}
                strokeWidth={1.7}
                className="text-brand-purple mb-6"
              />

              <div className="w-14 h-px bg-brand-mint mb-8"></div>

              <h3 className="font-display text-3xl lg:text-4xl tracking-tight uppercase">
                {item.title}
              </h3>

              <p className="mt-5 text-zinc-400 leading-relaxed">
                {item.text}
              </p>

            </div>
          </Reveal>
        );
      })}

    </div>

  </div>
</section>

      <EditorialMarquee />
      
      <section className="bg-brand-bg py-10 lg:py-12 px-6 lg:px-12 border-t border-zinc-900 overflow-hidden">
  <div className="max-w-[1600px] mx-auto">
    <div className="relative mt-0">

  <div
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
               w-[900px] h-[450px] rounded-full blur-3xl opacity-70"
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.35) 0%, rgba(17,245,186,.18) 55%, transparent 80%)"
    }}
  />
    <Reveal>
     {/* CHAPTER 03 — THE TOOLBOX */}
<section className="relative overflow-hidden bg-brand-bg pt-12 pb-24 lg:pt-16 lg:pb-32 border-t border-zinc-900">

  {/* Purple glow */}
  <div
    className="
      absolute
      left-[20%]
      top-1/2
      -translate-y-1/2
      w-[700px]
      h-[700px]
      rounded-full
      blur-[180px]
      opacity-[0.12]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(102,76,221,.8), transparent 70%)",
    }}
  />

  {/* Mint glow */}
  <div
    className="
      absolute
      right-[5%]
      bottom-[5%]
      w-[350px]
      h-[350px]
      rounded-full
      blur-[160px]
      opacity-[0.05]
      pointer-events-none
    "
    style={{
      background:
        "radial-gradient(circle, rgba(17,245,186,.8), transparent 70%)",
    }}
  />

  {/* SAME CONTAINER AS CHAPTER 02 */}
  <div className="relative z-10 max-w-[1600px] mx-auto">

    <Reveal className="mb-20">
      <p className="eyebrow mb-6">
        Chapter 03 — The Toolbox
      </p>

      <h2 className="font-display text-4xl lg:text-6xl tracking-tighter uppercase leading-[0.95] max-w-4xl">
        Every idea starts with
        <br />
        <span className="font-serif-accent text-brand-mint lowercase">
          the right tools.
        </span>
      </h2>

      <p className="mt-8 max-w-2xl text-zinc-400 text-base lg:text-lg leading-relaxed">
        Software doesn't build great work, people do. These are simply the tools
        I reach for every day while turning ideas into campaigns, websites and
        experiences that actually perform.
      </p>
    </Reveal>

    {/* YOUR TOOL BUBBLES START HERE */}

    {/* FLOATING TOOL FIELD */}
   <div className="
  relative
  mt-10
  h-[380px]
  sm:h-[420px]
  lg:h-[470px]
  max-w-[1150px]
  mx-auto
">

     {/* Google Search console*/}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/03/Whatsapp-Marketing-12-e1786427222663.png"
  alt="Google Search console"
  className="
    left-[10%] top-[12%]
    w-[52px] h-[52px]
    sm:w-[56px] sm:h-[56px]
    lg:w-[66px] lg:h-[66px]
  "
  delay={0}
/>

{/* Screaming frog */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/zzzz.png"
  alt="Screaming frog"
  className="
    left-[36%] top-[2%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.08}
/>

{/* Adobe After Effects */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/after-effects.png"
  alt="Adobe After Effects"
  className="
    right-[9%] top-[16%]
    w-[52px] h-[52px]
    sm:w-[56px] sm:h-[56px]
    lg:w-[66px] lg:h-[66px]
  "
  delay={0.16}
/>

{/* Mailchimp */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/20.png"
  alt="Mailchimp"
  className="
    left-[0%] top-[39%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.24}
/>

{/* Google Ads */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/google-adssss.png"
  alt="Google Ads"
  className="
    left-[36%] top-[59%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.24}
/>

{/* react */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/21.png"
  alt="React"
  className="
    left-[19%] top-[43%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.24}
/>

{/* Google Analytics */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/gggggg.png"
  alt="Google Analytics"
  className="
    left-[65%] top-[15%]
    w-[52px] h-[52px]
    sm:w-[56px] sm:h-[56px]
    lg:w-[66px] lg:h-[66px]
  "
  delay={0.32}
/>

{/* Meta */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/12.png"
  alt="Meta Ads"
  className="
    left-[48%] top-[30%]
    w-[52px] h-[52px]
    sm:w-[56px] sm:h-[56px]
    lg:w-[66px] lg:h-[66px]
  "
  delay={0.32}
/>

{/* Semrush */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/14.png"
  alt="Semrush"
  className="
    right-[17%] top-[49%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.4}
/>

{/* Claude */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/22.png"
  alt="Claude AI"
  className="
    right-[0%] top-[35%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.4}
/>

{/* Figma */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/15.png"
  alt="Figma"
  className="
    left-[11%] bottom-[9%]
    w-[50px] h-[50px]
    sm:w-[55px] sm:h-[55px]
    lg:w-[65px] lg:h-[65px]
  "
  delay={0.48}
/>

{/* ahrefs */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/White-Blue-Modern-Geometric-AI-Learning-Workshop-LinkedIn-Post.png"
  alt="ahrefs"
  className="
    left-[42%] bottom-[1%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.56}
/>

{/* Hubspot */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/17.png"
  alt="Hubspot"
  className="
    left-[56%] bottom-[26%]
    w-[48px] h-[48px]
    sm:w-[54px] sm:h-[54px]
    lg:w-[64px] lg:h-[64px]
  "
  delay={0.56}
/>

{/* Shopify */}
<ToolBubble
  src="https://dorado9-com-897128.hostingersite.com/wp-content/uploads/2026/08/16.png"
  alt="Shopify"
  className="
    right-[8%] bottom-[13%]
    w-[50px] h-[50px]
    sm:w-[55px] sm:h-[55px]
    lg:w-[65px] lg:h-[65px]
  "
  delay={0.64}
/>
    </div>

  </div>
</section>
    </Reveal></div>
    </div>
    </section>

      <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <p className="eyebrow mb-6">Values</p>
            <h2 className="font-display text-4xl lg:text-6xl tracking-tighter uppercase leading-[0.95] max-w-4xl">
              How we <span className="font-serif-accent text-brand-mint">work.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-10">
            {[
              { t: "Do the work", d: "Strategy is nothing without execution. We ship weekly." },
              { t: "Say the thing", d: "Direct feedback beats polite feedback. Always." },
              { t: "Compound", d: "Every campaign should build on the last. No resets." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="p-8 lg:p-10 border border-zinc-900 hover:border-brand-mint/40 transition-colors duration-500 min-h-[220px]" data-testid={`about-value-${i}`}>
                  <p className="font-serif-accent text-brand-mint text-5xl leading-none">{`0${i+1}`}</p>
                  <h3 className="font-display text-2xl mt-6 uppercase">{v.t}</h3>
                  <p className="text-zinc-400 mt-4 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900 text-center">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="eyebrow mb-6">Say hello</p>
            <h2 className="font-display text-4xl lg:text-6xl tracking-tighter uppercase leading-[0.95]">
              Coffee, chai, or a <span className="font-serif-accent text-brand-mint">quick call?</span>
            </h2>
            <Link to="/contact" className="btn-primary mt-10 inline-flex" data-testid="about-cta">Start a project →</Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
function ToolBubble({ src, alt, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 28,
        scale: 0.8,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      viewport={{
        once: true,
        amount: 0.2,
      }}
      transition={{
        duration: 0.65,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      whileHover={{
        y: -8,
        scale: 1.08,
        rotate: 2,
      }}
      className={`
        group
        absolute
        flex
        items-center
        justify-center
        overflow-hidden
        rounded-[30%]
        border
        border-white/[0.08]
        bg-[#1d1c24]/85
        backdrop-blur-xl
        shadow-[0_18px_60px_rgba(0,0,0,0.28)]
        transition-colors
        duration-500
        hover:border-brand-mint/35
        ${className}
      `}
    >
      {/* HOVER GLOW */}
      <div
        className="
          absolute
          inset-0
          rounded-[30%]
          opacity-0
          group-hover:opacity-100
          transition-opacity
          duration-500
          pointer-events-none
          z-20
        "
        style={{
          background:
            "radial-gradient(circle at 50% 30%, rgba(102,76,221,.20), transparent 68%)",
        }}
      />

      {/* LOGO */}
      <img
        src={src}
        alt={alt}
        draggable="false"
        className="
          relative
          z-10
          w-full
          h-full
          object-cover
          transition-transform
          duration-500
          group-hover:scale-[1.08]
        "
      />
    </motion.div>
  );
}